import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { COLORS } from '@utils/constants';

const categorySchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  type: z.string().min(1, 'Tipo é obrigatório'),
  color: z.string().default('#3B82F6'),
  icon: z.string().default('📁'),
  budget: z.number().optional().nullable(),
});

const typeOptions = [
  { value: 'expense', label: 'Despesa' },
  { value: 'income', label: 'Receita' },
];

const icons = ['🏠', '🍔', '🚗', '🎬', '🏥', '📚', '👕', '🎁', '💼', '✈️', '🛒', '💡', '📱', '🎮', '💰', '💻'];

const CategoryForm = ({ category, onSubmit, onCancel, isLoading }) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      type: 'expense',
      color: '#3B82F6',
      icon: '📁',
      budget: null,
    },
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');
  const selectedType = watch('type');

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        type: category.type,
        color: category.color || '#3B82F6',
        icon: category.icon || '📁',
        budget: category.budget,
      });
    }
  }, [category, reset]);

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input
        label="Nome da categoria"
        placeholder="Ex: Alimentação, Transporte, etc"
        error={errors.name?.message}
        {...register('name')}
      />

      <Select
        label="Tipo"
        options={typeOptions}
        error={errors.type?.message}
        disabled={!!category}
        {...register('type')}
      />

      {selectedType === 'expense' && (
        <Controller
          name="budget"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Orçamento mensal (opcional)"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? null)}
              onBlur={field.onBlur}
              hint="Defina um limite de gastos para esta categoria"
              error={errors.budget?.message}
            />
          )}
        />
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Ícone
        </label>
        <div className="flex flex-wrap gap-2">
          {icons.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setValue('icon', icon)}
              className={`w-10 h-10 rounded-lg border-2 text-xl flex items-center justify-center transition-colors ${
                selectedIcon === icon
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cor
        </label>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => setValue('color', color.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedColor === color.value
                  ? 'border-gray-900 dark:border-white scale-110'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {category ? 'Salvar' : 'Criar Categoria'}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;
